import { useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faExclamationTriangle,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import { useMissionArtifacts } from "@/services/api/agentic-v2/queries/useMissionArtifacts";

/**
 * Mission Detail's Artifacts tab. Lists artifacts produced by any task
 * in this mission, with filter pills (All / Needs review / Approved).
 *
 * Backed by ``GET /agentic-v2/missions/{id}/artifacts``.
 */
type ArtifactFilter = "all" | "needs_review" | "approved";

const FILTERS: { id: ArtifactFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "needs_review", label: "Needs review" },
  { id: "approved", label: "Approved" },
];

export interface ArtifactsTabProps {
  missionId: string;
  /** When false, the artefacts query does not run (inactive tab / background window). */
  enabled?: boolean;
}

export function ArtifactsTab({ missionId, enabled = true }: ArtifactsTabProps) {
  const [filter, setFilter] = useState<ArtifactFilter>("all");
  const artifactsQuery = useMissionArtifacts({ missionId, enabled });
  const artifacts = artifactsQuery.data ?? null;
  const loading = artifactsQuery.isLoading;
  const error = artifactsQuery.error;

  const visible = useMemo(() => {
    if (!artifacts) return [];
    if (filter === "all") return artifacts;
    if (filter === "approved") {
      return artifacts.filter((a) => a.approval?.state === "approved");
    }
    return artifacts.filter(
      (a) => !a.approval || a.approval.state === "pending",
    );
  }, [artifacts, filter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={clsx(
              "px-2.5 py-1 rounded-full text-xs transition-colors",
              filter === f.id
                ? "bg-indigo-500/15 text-indigo-200"
                : "bg-slate-800/40 text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-xs text-slate-500 text-center py-6">Loading…</p>
      )}

      {error && (
        <div className="rounded-xl bg-red-500/5 px-4 py-3 text-sm text-red-200 flex items-center gap-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error.message}
        </div>
      )}

      {!loading && !error && visible.length === 0 && (
        <div className="rounded-xl bg-slate-900/20 px-6 py-14 text-center">
          <FontAwesomeIcon
            icon={faFileLines}
            className="text-xl text-slate-600 mb-3"
          />
          <p className="text-sm text-slate-400">
            {artifacts && artifacts.length > 0
              ? "No artefacts match this filter."
              : "No artefacts yet."}
          </p>
          <p className="text-xs text-slate-600 mt-1">
            Work products produced by agents will appear here.
          </p>
        </div>
      )}

      {visible.length > 0 && (
        <div className="space-y-2">
          {visible.map((artifact) => (
            <ArtifactCard key={artifact._id} artifact={artifact} />
          ))}
        </div>
      )}
    </div>
  );
}
